/**
 * ET-aware time parsing for customer-facing windowed feeds.
 *
 * Lancaster, PA is the entire TruckTrackr market, so we treat
 * `America/New_York` as the canonical app timezone. Backend stores event
 * times in two formats:
 *   - schedule entries: NAIVE ISO ("2026-06-17T10:00:00") — interpreted ET
 *   - daily one-off / GCal-synced: TZ-AWARE ISO ("2026-06-17T10:00:00-04:00")
 *
 * `new Date(naiveIso).getTime()` parses naive strings as the user's
 * *device* local time. That's wrong for any customer outside ET (e.g.
 * California snowbird browsing the Lancaster map sees an event linger 3h
 * longer than it should). This helper normalises both formats to a UTC
 * epoch ms by treating naive strings as ET wall-clock time.
 *
 * Uses `Intl.DateTimeFormat` with `timeZoneName: "shortOffset"` to get
 * a DST-aware ET offset on the fly — no hardcoded EST/EDT.
 */

function _etOffsetForInstant(instantMs) {
  // Returns an ISO offset string like "-04:00" (EDT) or "-05:00" (EST)
  // for whatever the ET offset is on the given instant. DST-aware.
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York",
      timeZoneName: "shortOffset",
    }).formatToParts(new Date(instantMs));
    const tzName = parts.find((p) => p.type === "timeZoneName")?.value || "";
    // "GMT-4" or "GMT-5" — sometimes "GMT-04:00"
    const m = tzName.match(/GMT([+-])(\d{1,2})(?::?(\d{2}))?/);
    if (!m) return "-04:00"; // safe fallback (EDT)
    const sign = m[1];
    const hh = String(parseInt(m[2], 10)).padStart(2, "0");
    const mm = (m[3] || "00").padStart(2, "0");
    return `${sign}${hh}:${mm}`;
  } catch {
    return "-04:00";
  }
}

/**
 * Parse an event time string (naive OR TZ-aware ISO) to UTC epoch ms,
 * treating naive strings as `America/New_York` local time.
 *
 * Returns `NaN` for unparseable inputs.
 */
export function etTimeMs(s) {
  if (!s || typeof s !== "string") return NaN;
  // TZ already present? Trust it — JS Date handles tz-aware ISO correctly.
  if (/[Zz]$|[+-]\d{2}:?\d{2}$/.test(s)) {
    const t = new Date(s).getTime();
    return Number.isFinite(t) ? t : NaN;
  }
  // Naive — treat as ET. First parse as UTC to get an approximate instant,
  // then use that instant to look up ET's DST-aware offset, then re-parse
  // with the explicit offset. This handles DST transitions correctly
  // (the ET offset on June 17 = -04:00; on Jan 17 = -05:00).
  const approxUtcMs = Date.parse(s + "Z");
  if (!Number.isFinite(approxUtcMs)) return NaN;
  const offset = _etOffsetForInstant(approxUtcMs);
  const t = new Date(s + offset).getTime();
  return Number.isFinite(t) ? t : NaN;
}

const ET_TZ = "America/New_York";

/** Format an event time string with Intl options, ALWAYS pinned to
 *  America/New_York regardless of the viewer's device timezone. */
export function fmtET(iso, opts = {}) {
  const ms = etTimeMs(iso);
  if (!Number.isFinite(ms)) return "";
  try {
    return new Intl.DateTimeFormat("en-US", { timeZone: ET_TZ, ...opts }).format(new Date(ms));
  } catch {
    return "";
  }
}

/** "YYYY-MM-DD" calendar date of the event in ET. */
export function etDateKey(iso) {
  const ms = etTimeMs(iso);
  if (!Number.isFinite(ms)) return "";
  return new Intl.DateTimeFormat("en-CA", { timeZone: ET_TZ }).format(new Date(ms));
}

/** "YYYY-MM-DD" ET date for an epoch-ms instant, offset by N days. */
export function etDateKeyFromMs(ms, offsetDays = 0) {
  return new Intl.DateTimeFormat("en-CA", { timeZone: ET_TZ }).format(
    new Date(ms + offsetDays * 86400000)
  );
}

const _WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** ET weekday index for an event time: 0 = Sunday … 6 = Saturday. */
export function etWeekdayIndex(iso) {
  return _WEEKDAYS.indexOf(fmtET(iso, { weekday: "short" }));
}

/** Format an epoch-ms instant as a naive ET wall-clock string
 *  "YYYY-MM-DDTHH:MM:SS" — the app's storage convention for naive times. */
export function etWallString(ms) {
  if (!Number.isFinite(ms)) return "";
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: ET_TZ,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
  }).formatToParts(new Date(ms));
  const p = Object.fromEntries(parts.map((x) => [x.type, x.value]));
  const hh = p.hour === "24" ? "00" : p.hour;
  return `${p.year}-${p.month}-${p.day}T${hh}:${p.minute}:${p.second}`;
}
